import React, { useState } from "react";
import { Input, Button, Radio, message, Modal, Spin } from "antd";
import { CheckCircleOutlined, MobileOutlined, MailOutlined } from "@ant-design/icons";
import {
  apiInitiateLinkedReg,
  apiSendLinkedOtp,
  apiVerifyLinkedOtp,
} from "services/registerService";

/**
 * LinkedRegAuth
 * OTP verification flow for linked (extra program) registrations.
 * Stages:
 *   0 - Email entry
 *   1 - Choose delivery method (email / sms)
 *   2 - Enter OTP code
 *   3 - Select participants
 */
const LinkedRegAuth = ({ registrationId, onVerified }) => {
  const [stage, setStage] = useState(0);
  const [isBusy, setIsBusy] = useState(false);

  const [email, setEmail] = useState("");
  const [maskedPhone, setMaskedPhone] = useState("");
  const [hasPhone, setHasPhone] = useState(false);
  const [deliveryMethod, setDeliveryMethod] = useState("email");
  const [code, setCode] = useState("");
  const [participants, setParticipants] = useState([]);
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [parentTransactionId, setParentTransactionId] = useState(null);
  const [groupList, setGroupList] = useState([]);

  const handleEmailSubmit = async () => {
    if (!email || !email.includes("@")) {
      message.error("Please enter a valid email address");
      return;
    }
    setIsBusy(true);
    try {
      const res = await apiInitiateLinkedReg(email, registrationId);
      if (res.result) {
        setMaskedPhone(res.masked_phone || "");
        setHasPhone(res.has_phone || false);
        setDeliveryMethod(res.has_phone ? "sms" : "email");
        setStage(1);
      } else {
        Modal.warning({
          title: "No Registrations Found",
          content: "No registrations found with that email address. Please try another email address.",
          okText: "OK",
        });
      }
    } catch {
      Modal.warning({
        title: "No Registrations Found",
        content: "No registrations found with that email address. Please try another email address.",
        okText: "OK",
      });
    } finally {
      setIsBusy(false);
    }
  };

  const handleSendCode = async () => {
    setIsBusy(true);
    try {
      const res = await apiSendLinkedOtp(email, registrationId, deliveryMethod);
      if (res.result) {
        message.success(deliveryMethod === "sms" ? `Code sent to ${maskedPhone}` : `Code sent to ${email}`);
        setCode("");
        setStage(2);
      } else {
        message.error(res.message || "Failed to send code. Please try again.");
      }
    } catch {
      message.error("Something went wrong. Please try again.");
    } finally {
      setIsBusy(false);
    }
  };

  const handleVerifyCode = async () => {
    if (code.length !== 6) {
      message.error("Please enter the 6-digit code");
      return;
    }
    setIsBusy(true);
    try {
      const res = await apiVerifyLinkedOtp(email, code, registrationId);
      if (res.result) {
        setParticipants(res.participants || []);
        setParentTransactionId(res.parent_transaction_id || null);
        setGroupList(res.group_list || []);
        // Start with nothing selected — user must choose
        setSelectedKeys([]);
        setStage(3);
      } else {
        message.error(res.message || "Incorrect code. Please try again.");
      }
    } catch {
      message.error("Something went wrong. Please try again.");
    } finally {
      setIsBusy(false);
    }
  };

  const handleContinue = () => {
    const selected = participants.filter((_, i) => selectedKeys.includes(i));
    if (selected.length === 0) {
      message.error("Please select at least one participant");
      return;
    }
    onVerified({
      participants: selected,
      email,
      parentTransactionId,
      groupList,
    });
  };

  const toggleParticipant = (index) => {
    setSelectedKeys((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
    );
  };

  return (
    <Spin spinning={isBusy}>
      <div style={{ maxWidth: 480, margin: "40px auto", padding: "0 16px" }}>

        {/* Stage 0: Email */}
        {stage === 0 && (
          <div>
            <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 8 }}>
              Enter your registration email
            </div>
            <div style={{ color: "#666", marginBottom: 16 }}>
              Enter the email address you used when you originally registered.
            </div>
            <Input
              size="large"
              placeholder="Email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onPressEnter={handleEmailSubmit}
              autoFocus
            />
            <Button
              type="primary"
              size="large"
              style={{ marginTop: 16, width: "100%" }}
              onClick={handleEmailSubmit}
            >
              Continue
            </Button>
          </div>
        )}

        {/* Stage 1: Choose delivery method */}
        {stage === 1 && (
          <div>
            <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 8 }}>
              How would you like to receive your code?
            </div>
            <Radio.Group
              value={deliveryMethod}
              onChange={(e) => setDeliveryMethod(e.target.value)}
              style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 24 }}
            >
              {hasPhone && (
                <Radio value="sms">
                  <MobileOutlined style={{ marginRight: 6 }} />
                  Text to {maskedPhone}
                </Radio>
              )}
              <Radio value="email">
                <MailOutlined style={{ marginRight: 6 }} />
                Email to {email}
              </Radio>
            </Radio.Group>
            <Button
              type="primary"
              size="large"
              style={{ width: "100%" }}
              onClick={handleSendCode}
            >
              Send Code
            </Button>
            <Button
              size="large"
              style={{ marginTop: 8, width: "100%" }}
              onClick={() => setStage(0)}
            >
              Back
            </Button>
          </div>
        )}

        {/* Stage 2: Enter OTP */}
        {stage === 2 && (
          <div>
            <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 8 }}>
              Enter your 6-digit code
            </div>
            <div style={{ color: "#666", marginBottom: 16 }}>
              {deliveryMethod === "sms"
                ? `A code was sent to ${maskedPhone}`
                : `A code was sent to ${email}`}
            </div>
            <Input
              size="large"
              maxLength={6}
              placeholder="000000"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              onPressEnter={handleVerifyCode}
              style={{ letterSpacing: 8, fontSize: 22, textAlign: "center" }}
              autoFocus
            />
            <Button
              type="primary"
              size="large"
              style={{ marginTop: 16, width: "100%" }}
              onClick={handleVerifyCode}
            >
              Verify
            </Button>
            <Button
              size="large"
              style={{ marginTop: 8, width: "100%" }}
              onClick={handleSendCode}
            >
              Resend Code
            </Button>
            <Button
              size="large"
              style={{ marginTop: 8, width: "100%" }}
              onClick={() => setStage(1)}
            >
              Back
            </Button>
          </div>
        )}

        {/* Stage 3: Select participants */}
        {stage === 3 && (
          <div>
            <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 8 }}>
              Select participant(s)
            </div>
            <div style={{ color: "#666", marginBottom: 16 }}>
              Choose who you are adding extra programs for.
            </div>
            {participants.map((p, i) => (
              <div
                key={i}
                onClick={() => toggleParticipant(i)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  padding: "12px 16px",
                  marginBottom: 8,
                  border: selectedKeys.includes(i) ? "2px solid #1890ff" : "2px solid #d9d9d9",
                  borderRadius: 8,
                  cursor: "pointer",
                  background: selectedKeys.includes(i) ? "#e6f7ff" : "#fff",
                }}
              >
                <CheckCircleOutlined
                  style={{
                    fontSize: 20,
                    color: selectedKeys.includes(i) ? "#1890ff" : "#d9d9d9",
                    marginRight: 12,
                  }}
                />
                <div>
                  <div style={{ fontWeight: 600 }}>
                    {p.camper_first_name} {p.camper_last_name}
                  </div>
                  {p.camper_age && (
                    <div style={{ color: "#666", fontSize: 13 }}>Age: {p.camper_age}</div>
                  )}
                </div>
              </div>
            ))}
            <Button
              type="primary"
              size="large"
              style={{ marginTop: 16, width: "100%" }}
              onClick={handleContinue}
            >
              Continue
            </Button>
          </div>
        )}
      </div>
    </Spin>
  );
};

export default LinkedRegAuth;
